import { Button, Col, Divider, Form, Modal, notification, Row, Spin, Tag } from "antd";
import { FunctionComponent, useEffect, useState } from "react"
import { ApiCallbacks, buildUrlWithParams, BULLETIN_ENDPOINT, BULLETIN_EXISTING_ENDPOINT, BULLETINS_ELEVE_ENDPOINT, handleApiCall, MATIERES_ENDPOINT } from "../../services/services";
import dayjs from "dayjs";
import { BulletinDtoB, BulletinDtoF, MatiereNoteEnum, TypeMatiereEnum } from "../../services/classe";
import { SelectFormItem } from "../common/SelectFormItem";
import { EleveEnrichedDto } from "../../services/eleve";
import { InputFormItem } from "../common/InputFormItem";
import TextArea from "antd/es/input/TextArea";
import { InputNumberFormItem } from "../common/InputNumberFormItem";
import { firstLettertoUpperCase, prepareBulletinBeforeSave } from '../../utils/FormUtils';
import { useMatieresStore } from "../stores/useMatieresStore";
import { DatePickerFormItem } from "../common/DatePickerFormItem";
import useApi, { APICallResult } from "../../hooks/useApi";


export type ModalBulletinProps = {
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    isCreation: boolean,
    bulletin?: BulletinDtoF,
    annees: number[],
    eleve?: EleveEnrichedDto,
}

export const ModalBulletin: FunctionComponent<ModalBulletinProps> = ({ open, setOpen, isCreation, bulletin, annees, eleve }) => {
    const { execute, isLoading } = useApi();
    const [form] = Form.useForm();
    const { getMatieresByType } = useMatieresStore();

    const close = () => {
        form.resetFields();
        setOpen(false);
    };

    const onValider = async (values: any) => {
        const { notes, remarques, ...otherFields } = values;
        const bulletinMatieres = Object.keys(notes || {}).map((code) => ({
            code,
            note: notes[code],
            remarque: remarques?.[code]
        }));
        const bulletinToSave: BulletinDtoB = prepareBulletinBeforeSave({
            ...bulletin,
            ...otherFields,
            bulletinMatieres
        });
        let result = null;
        if (isCreation) {
            result = await execute<BulletinDtoB>({ method: "POST", url: BULLETIN_ENDPOINT, data: bulletinToSave });
        } else {
            result = await execute<BulletinDtoB>({ method: "PUT", url: buildUrlWithParams(BULLETIN_EXISTING_ENDPOINT, { id: bulletin?.id }), data: bulletinToSave });
        }
        if (result.success) {
            confirmSaveSuccess();
            setOpen(false);
        }
    }

    const getTitre = () => {
        return isCreation ? "Création d'un nouveau bulletin" : "Modification d'un bulletin";
    }

    useEffect(() => {
        if (bulletin) {
            const notes: Record<string, string> = {};
            const remarques: Record<string, string> = {};
            bulletin.bulletinMatieres?.forEach((matiere) => {
                notes[matiere.code.toString()] = matiere.note;
                remarques[matiere.code.toString()] = matiere.remarque;
            });
            form.setFieldsValue({ ...bulletin, notes, remarques });
        }
    }, [bulletin]);

    function confirmSaveSuccess() {
        notification.success({ message: "Le bulletin a bien été enregistré" });
        close();
    }

    function getNotesOptions() {
        return [{ value: MatiereNoteEnum.A, label: <Tag color="green">Acquis</Tag> },
        { value: MatiereNoteEnum.EA, label: <Tag color="orange">En cours d'acquisition</Tag> },
        { value: MatiereNoteEnum.NA, label: <Tag color="red">Non acquis</Tag> }];
    }

    return (<Modal title={getTitre()} open={open} width={1000} onCancel={close}
        footer={<>
            <Button onClick={close} type="primary">Annuler</Button>
            <Button htmlType="submit" type="primary" onClick={() => form.submit()} >Valider</Button>
        </>} >
        <Form
            name="periode"
            autoComplete="off"
            form={form}
            onFinish={onValider}
        >
            <Spin spinning={isLoading}>
                <Divider orientation="left">Informations générales</Divider>
                <div>Nom de l'élève: <Tag color="orange" className="m-left-10">{eleve?.prenom} {eleve?.nom}</Tag></div><br />
                <Row gutter={[16, 32]}>
                    <Col span={8}>
                        <SelectFormItem label="Mois" name="mois"
                            options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(mois => ({ value: mois, label: firstLettertoUpperCase(dayjs().month(mois - 1).format("MMMM")) }))}
                            rules={[{ required: true, message: "Veuillez sélectionner le mois" }]} />
                    </Col>
                    <Col span={12}>
                        <SelectFormItem label="Annee" name="annee" options={annees.map(annee => ({ value: annee, label: annee }))}
                            rules={[{ required: true, message: "Veuillez sélectionner l'année" }]} />
                    </Col>
                </Row>
                <Row gutter={[16, 32]}>
                    <Col span={8}>
                        <InputNumberFormItem name="nbAbsences" label="Nombre d'absences" rules={[{ required: true, message: "Veuillez indiquer le nombre d'absences" }]}
                            min={0} />
                    </Col>
                </Row>
                <Divider orientation="left">Notes</Divider>
                {getMatieresByType(TypeMatiereEnum.ENFANT).map((matiere) => (
                    <Row key={matiere.code} gutter={[32, 32]}>
                        <Col span={4}>
                            <Tag color="geekblue">{matiere.fr}</Tag>
                        </Col>
                        <Col span={9}>
                            <SelectFormItem name={"notes." + matiere.code} label="Sélectionnez une note" options={getNotesOptions()}
                                rules={[{ required: true, message: "Veuillez sélectionner une note" }]} />
                        </Col>
                        <Col span={11}>
                            <InputFormItem name={"remarques." + matiere.code} label="Remarques" />
                        </Col>
                    </Row>
                ))}
                <Divider orientation="left">Appréciation générale</Divider>
                <Row gutter={[16, 32]}>
                    <Col span={6}>
                        <DatePickerFormItem name="dateBulletin" label="Date" rules={[{ required: true, message: "Veuillez indiquer la date du bulletin (apparaîtra sur le document PDF)" }]} />
                    </Col>
                    <Col span={18}>
                        <Form.Item
                            label="Appréciation"
                            name="appreciation"
                            rules={[{ required: true, message: "Veuillez indiquer votre appréciation générale" }]}
                        >
                            <TextArea rows={4} placeholder="Veuillez saisir votre appréciation générale ici..." />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>
        </Form>
    </Modal>);

}